<script setup lang="ts">
/**
 * PBadge — étiquette compacte sémantique (status, tier, count).
 *
 * Variants : neutral (défaut), success (euca), warning (amber), danger (brick),
 * info (ink-4), brand (olive accent). Le rendu est éditorial : font mono
 * uppercase tracking-wider, bordure 1px, radius 2px (pas pill).
 *
 * Pour un dot de statut inline (ex: « En attente · ⬤ »), voir PStatusDot
 * — pour l'instant inlined via slot.
 */
interface Props {
    variant?: 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'brand'
    size?: 'sm' | 'md'
}

withDefaults(defineProps<Props>(), {
    variant: 'neutral',
    size: 'md',
})
</script>

<template>
    <span :class="['p-badge', `p-badge--${variant}`, `p-badge--${size}`]">
        <slot />
    </span>
</template>

<style scoped>
.p-badge {
    display: inline-flex;
    align-items: center;
    gap: var(--p-space-1);
    border-radius: var(--p-radius-sm);
    border: 1px solid transparent;
    font-family: var(--p-font-mono);
    font-weight: var(--p-font-weight-medium);
    text-transform: uppercase;
    letter-spacing: var(--p-tracking-wider);
    white-space: nowrap;
    line-height: 1.4;
}

.p-badge--sm {
    padding: 1px 6px;
    font-size: 9px;
}
.p-badge--md {
    padding: 2px 8px;
    font-size: var(--p-text-2xs);
}

.p-badge--neutral {
    background: transparent;
    color: var(--p-color-ink-5);
    border-color: var(--p-color-line);
}
.p-badge--success {
    background: var(--p-color-euca-bg);
    color: var(--p-color-euca);
    border-color: var(--p-color-euca-soft);
}
.p-badge--warning {
    background: var(--p-color-amber-bg);
    color: var(--p-color-amber);
    border-color: var(--p-color-amber-soft);
}
.p-badge--danger {
    background: var(--p-color-brick-bg);
    color: var(--p-color-brick);
    border-color: var(--p-color-brick-soft);
}
.p-badge--info {
    background: var(--p-color-surface-alt);
    color: var(--p-color-ink-4);
    border-color: var(--p-color-line);
}
.p-badge--brand {
    background: var(--p-color-olive-bg);
    color: var(--p-color-olive);
    border-color: var(--p-color-olive-soft);
}
</style>
